import React, { createContext, useContext, useState, useEffect } from 'react';
import { Property } from '../types';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface PropertyContextType {
  properties: Property[];
  loading: boolean;
  error: string | null;
  addProperty: (property: Omit<Property, 'id' | 'createdAt' | 'updatedAt'>) => void;
  getProperty: (id: string) => Property | undefined;
  toggleFavorite: (propertyId: string) => Promise<void>;
  favorites: string[];
}

const PropertyContext = createContext<PropertyContextType>({} as PropertyContextType);

export const PropertyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setError(null);
      try {
        const { items } = await apiService.getPublicListings();
        const mapped: Property[] = items.map((l: any) => ({
          id: l.id,
          title: l.title,
          description: l.description ?? '',
          price: l.price ?? 0,
          address: {
            street: l.address ?? '',
            city: l.city ?? '',
            state: '',
            zipCode: l.zip_code ?? l.zipCode ?? '',
            country: l.country ?? ''
          },
          images: (l.images ?? []).map((img: any) => img.public_url ?? img.publicUrl).filter(Boolean),
          type: 'apartment',
          status: (l.purpose === 'RENT' ? 'rent' : 'sale'),
          features: {
            bedrooms: l.rooms ?? 0,
            bathrooms: l.bathrooms ?? 0,
            area: Number(l.area_m2 ?? l.areaM2 ?? 0),
            garage: false,
            yearBuilt: new Date(l.created_at ?? l.createdAt ?? new Date()).getFullYear()
          },
          amenities: [],
          contactInfo: { name: '', phone: '', email: '' },
          owner: l.owner_user_id ?? l.ownerUserId ?? '',
          createdAt: l.created_at ?? l.createdAt ?? new Date().toISOString(),
          updatedAt: l.updated_at ?? l.updatedAt ?? new Date().toISOString()
        }));
        setProperties(mapped);
      } catch (err: any) {
        setError(err?.message || 'Failed to fetch properties');
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const addProperty = (property: Omit<Property, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newProperty: Property = {
      ...property,
      id: Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setProperties([...properties, newProperty]);
  };

  const getProperty = (id: string) => {
    return properties.find(property => property.id === id);
  };

  // Sync favorites from user object when user changes
  useEffect(() => {
    if (user?.favorites) {
      setFavorites(user.favorites);
    }
  }, [user?.favorites]);

  const toggleFavorite = async (propertyId: string) => {
    try {
      if (favorites.includes(propertyId)) {
        // Remove from favorites
        await apiService.removeFromFavorites(propertyId);
        setFavorites(favorites.filter(id => id !== propertyId));
      } else {
        // Add to favorites
        await apiService.addToFavorites(propertyId);
        setFavorites([...favorites, propertyId]);
      }
      // Refresh user to get updated favorites list
      await refreshUser();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Revert local state on error
      if (!favorites.includes(propertyId)) {
        setFavorites(favorites.filter(id => id !== propertyId));
      } else {
        setFavorites([...favorites, propertyId]);
      }
    }
  };

  return (
    <PropertyContext.Provider
      value={{
        properties,
        loading,
        error,
        addProperty,
        getProperty,
        toggleFavorite,
        favorites
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperty = () => useContext(PropertyContext);
